import { Component, Input, Output, OnInit, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UUID } from 'angular2-uuid';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { FormBuilderUDFService } from 'src/app/services/form-builder-udf-service';

@Component({
  selector: 'app-dropdown-property',
  templateUrl: './dropdown-property.component.html',
  styleUrls: ['./dropdown-property.component.scss']
})
export class DropdownPropertyComponent implements OnInit, OnChanges {
  private _attributes: any;
  public varcharAttributes: Array<any> = [];

  // Tracks the currently active element id
  private _currentComponentId: string | null = null;

  // Set of element IDs whose field_name was intentionally typed by the user
  private _fieldNameLockedIds = new Set<string>();

  datasource: string;

  @Input() component: any;
  @Input() set attributes(value: any) {
    this._attributes = value;
    if (this._attributes) {
      this.varcharAttributes = _.sortBy(
        this._attributes.filter((att: any) => att.type === 'varchar'),
        ['name']
      );
    }
  }

  get attributes(): any {
    return this._attributes;
  }

  @Output() onBindVariableChange: EventEmitter<any> = new EventEmitter();

  apiList: any;
  apiEndpoint: any;
  filteredAPIS: any;

  constructor(
    private translate: TranslateService,
    private formService: FormBuilderUDFService,
    private toaster: ToastrService
  ) {}

  ngOnInit() {
    this.getAPIList();
   
  }

  ngOnChanges(changes: SimpleChanges) {
   
  }

  

  /**
   * Called from (ngModelChange) on the label input.
   * Auto-generates field_name from label ONLY if user hasn't manually locked it.
   */
  onLabelChange(label: string) {
    const id = this.component?.id;
    if (!id || this._fieldNameLockedIds.has(id)) return;

    this.component.element.field_name = label
      ? label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .replace(/_+/g, '_')
          .substring(0, 100)
      : '';
  }

  // ─── Option Value Auto-gen Logic ────────────────────────────────────────────

  /**
   * Called from (input) on each option's Text field.
   * Auto-generates the option's value from text, unless user has manually locked it.
   */
  onOptionTextChange(option: any) {
    if (option._valueLocked) return;

    option.value = option.text
      ? option.text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
          .replace(/_+/g, '_')
          .substring(0, 100)
      : '';
  }

  /**
   * Called from (input) on each option's Value field.
   * Locks the value so text changes no longer overwrite it.
   * Unlocks if user clears the value field.
   */
  onOptionValueInput(option: any) {
    option._valueLocked = !!option.value;
  }

  // ─── Options CRUD ────────────────────────────────────────────────────────────

  setAsDefault($event: any) {
    this.component.element.options.forEach((v: any) => {
      v.is_selected = v.id == $event.target.value;
    });
  }

  addOption() {
    this.component.element.options.push({
      id: UUID.UUID(),
      text: this.translate.instant('Option') + ' ' + (this.component.element.options.length + 1),
      value: '',          // blank so auto-gen kicks in when user types text
      is_selected: false,
      _valueLocked: false // not manually set yet
    });
  }

  removeOption(optionId: any) {
    this.component.element.options = this.component.element.options.filter(
      (v: any) => v.id != optionId
    );
  }

  // ─── Other ───────────────────────────────────────────────────────────────────

  handleOnBindVariableChange($event: any) {
    this.onBindVariableChange.emit({
      value: $event,
      id: this.component.id
    });
  }

  onChangeDatasourcetype(selected: any) {
    this.datasource = selected.value;
    if (this.datasource === 'url') {
      this.getAPIList();
    }
  }

  onChangeAPI(apiId: any) {
    const APIDetails = this.apiList.find((api: any) => (api.id ?? api.Id) === apiId);
    if (APIDetails) {
      const id = APIDetails.id ?? APIDetails.Id ?? null;
      const ext = APIDetails.APIIsExternal;
      const isExternal = ext === true || ext === 1 || ext === 'true' || ext === 'True' || ext === '1';

      this.component.element.resultsetfieldName = APIDetails.APIResponseColumns ?? '';
      this.component.element.resultsetName = APIDetails.APIResponseResultSetName ?? '';
      this.component.element.datasourceParamaters = APIDetails.APIPayloadParameter ?? '';
      this.component.element.apiDirectoryId = id;
      this.component.element.isExternalAPI = isExternal;

      if (isExternal && id) {
        // Store the proxy GET endpoint as the datasource URL so getDataFromAPI
        // calls it directly — no special routing flags needed at render time.
        this.component.element.datasourceUrl = `FormBuilder/GetExternalData?apiDirectoryId=${id}`;
        this.component.element.requestType = 'GET';
      } else {
        this.component.element.datasourceUrl = APIDetails.APIURL ?? '';
        this.component.element.requestType = APIDetails.APIRequestType ?? '';
      }
    }
  }

  getAPIList() {
    this.formService.getAPIList().subscribe({
      next: (data: any) => {
        if (data) {
          this.apiList = data.Table;
          this.filteredAPIS = this.apiList.slice();
        }
      },
      error: () => {
        this.toaster.error('Error fetching API list');
      }
    });
  }
  // Remove forbidden special characters from field_name (SQL Server safe)
  sanitizeFieldName() {
    if (this.component && this.component.element && typeof this.component.element.field_name === 'string') {
      // Only allow letters, numbers, and underscores, must start with letter or underscore
      let val = this.component.element.field_name;
      // Remove forbidden special characters ($&^#@ and others)
      val = val.replace(/[^A-Za-z0-9_]/g, '');
      // Ensure first character is letter or underscore
      if (!/^[A-Za-z_]/.test(val)) {
        val = val.replace(/^[^A-Za-z_]+/, '');
      }
      // Limit to 100 chars
      this.component.element.field_name = val.substring(0, 100);
    }
  }

    // Prevent forbidden special characters from being entered
  preventSpecialChar(event: KeyboardEvent) {
    const allowed = /^[A-Za-z0-9_]$/;
    // Allow navigation, backspace, delete, tab, arrows, home, end
    if (
      event.ctrlKey || event.metaKey ||
      [8, 9, 13, 16, 17, 18, 20, 27, 35, 36, 37, 38, 39, 40, 46].includes(event.keyCode)
    ) {
      return;
    }
    if (!allowed.test(event.key)) {
      event.preventDefault();
    }
  }

  // Filter forbidden characters on paste or input
  filterSpecialCharOnInput() {
    if (this.component && this.component.element && typeof this.component.element.field_name === 'string') {
      let val = this.component.element.field_name;
      val = val.replace(/[^A-Za-z0-9_]/g, '');
      // Ensure first character is letter or underscore
      if (!/^[A-Za-z_]/.test(val)) {
        val = val.replace(/^[^A-Za-z_]+/, '');
      }
      this.component.element.field_name = val.substring(0, 100);
    }
  }

}